import {
  Box,
  SimpleGrid,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  Spinner,
  Badge,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import React from 'react';
import {
  MdAttachMoney,
  MdPerson,
  MdBookOnline,
  MdCheckCircle,
  MdPending,
  MdCancel,
  MdTempleHindu,
  MdOutlineShoppingCart,
} from 'react-icons/md';
import { getBookingStats } from '../../../Service/PoojaBookingService';
import { getPoojaCount } from '../../../Service/PoojaService';
import { collection, getCountFromServer, getFirestore } from 'firebase/firestore';
import app from '../../../config/firebaseConfig';
import Card from 'components/card/Card';

const db = getFirestore(app);

export default function Dashboard() {
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [stats, setStats] = React.useState(null);
  const [poojaCount, setPoojaCount] = React.useState(0);
  const [userCount, setUserCount] = React.useState(0);
  const [productCount, setProductCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [bookingStats, poojaResult, usersSnap, productsSnap] = await Promise.all([
          getBookingStats(),
          getPoojaCount(),
          getCountFromServer(collection(db, 'users')).catch(() => ({ data: () => ({ count: 0 }) })),
          getCountFromServer(collection(db, 'products')).catch(() => ({ data: () => ({ count: 0 }) })),
        ]);
        setStats(bookingStats);
        setPoojaCount(poojaResult.count || 0);
        setUserCount(usersSnap.data().count || 0);
        setProductCount(productsSnap.data().count || 0);
      } catch (e) {
        console.error('Dashboard load error:', e);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Flex justify="center" align="center" h="300px">
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px" mb="20px">
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg="linear-gradient(135deg, #FF6B35 0%, #F7C59F 100%)"
              icon={<Icon w="28px" h="28px" as={MdAttachMoney} color="white" />} />
          }
          name="Total Revenue"
          value={`₹${(stats?.totalRevenue || 0).toLocaleString('en-IN')}`}
        />
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg="linear-gradient(135deg, #667EEA 0%, #764BA2 100%)"
              icon={<Icon w="28px" h="28px" as={MdBookOnline} color="white" />} />
          }
          name="Total Bookings"
          value={stats?.total || 0}
        />
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg="linear-gradient(135deg, #11998E 0%, #38EF7D 100%)"
              icon={<Icon w="28px" h="28px" as={MdTempleHindu} color="white" />} />
          }
          name="Total Poojas"
          value={poojaCount}
        />
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg="linear-gradient(135deg, #FC5C7D 0%, #6A82FB 100%)"
              icon={<Icon w="28px" h="28px" as={MdPerson} color="white" />} />
          }
          name="Customers"
          value={userCount}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px" mb="20px">
        {[
          { label: 'Confirmed', value: stats?.confirmed || 0, icon: MdCheckCircle, bg: 'green.50', color: 'green.500' },
          { label: 'Pending', value: stats?.pending || 0, icon: MdPending, bg: 'yellow.50', color: 'yellow.500' },
          { label: 'Paid', value: stats?.paid || 0, icon: MdAttachMoney, bg: 'blue.50', color: 'blue.500' },
          { label: 'Cancelled', value: stats?.cancelled || 0, icon: MdCancel, bg: 'red.50', color: 'red.500' },
        ].map(item => (
          <Card key={item.label} py="15px" px="20px">
            <HStack spacing={4}>
              <Box w="48px" h="48px" borderRadius="12px" bg={item.bg} display="flex" alignItems="center" justifyContent="center">
                <Icon as={item.icon} color={item.color} w="24px" h="24px" />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="500">{item.label}</Text>
                <Text fontSize="2xl" fontWeight="800" color={textColor}>{item.value}</Text>
              </VStack>
            </HStack>
          </Card>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
        <Card p="20px">
          <Text fontSize="lg" fontWeight="700" mb={4} color={textColor}>📊 Booking Overview</Text>
          <VStack spacing={3} align="stretch">
            {[
              { label: 'Total Bookings', value: stats?.total || 0, color: 'brand' },
              { label: 'Confirmed', value: stats?.confirmed || 0, color: 'green' },
              { label: 'Pending', value: stats?.pending || 0, color: 'yellow' },
              { label: 'Cancelled', value: stats?.cancelled || 0, color: 'red' },
              { label: 'Payments Received', value: stats?.paid || 0, color: 'blue' },
            ].map(item => (
              <HStack key={item.label} justify="space-between">
                <Text fontSize="sm" color="gray.500">{item.label}</Text>
                <Badge colorScheme={item.color} fontSize="sm" px={3} py={1} borderRadius="full">{item.value}</Badge>
              </HStack>
            ))}
          </VStack>
        </Card>

        <Card p="20px">
          <Text fontSize="lg" fontWeight="700" mb={4} color={textColor}>📦 Platform Summary</Text>
          <VStack spacing={3} align="stretch">
            {[
              { label: 'Total Poojas', value: poojaCount, icon: MdTempleHindu, color: 'orange' },
              { label: 'Total Customers', value: userCount, icon: MdPerson, color: 'purple' },
              { label: 'Total Products', value: productCount, icon: MdOutlineShoppingCart, color: 'cyan' },
            ].map(item => (
              <HStack key={item.label} justify="space-between">
                <HStack>
                  <Icon as={item.icon} color={`${item.color}.400`} />
                  <Text fontSize="sm" color="gray.500">{item.label}</Text>
                </HStack>
                <Badge colorScheme={item.color} fontSize="sm" px={3} py={1} borderRadius="full">{item.value}</Badge>
              </HStack>
            ))}
            <Divider />
            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="700" color={textColor}>Total Revenue</Text>
              <Text fontSize="lg" fontWeight="800" color="green.500">
                ₹{(stats?.totalRevenue || 0).toLocaleString('en-IN')}
              </Text>
            </HStack>
          </VStack>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
